import { NavLink } from 'react-router-dom';
import { Home, Settings, LogOut, Users, ClipboardList, FileText, UserCheck, ShieldCheck, Truck, Warehouse, CalendarDays, CalendarClock, Receipt, Map, DollarSign, HardHat, BarChart3 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useIsPhone } from '../../hooks/useIsPhone';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2f371d17-d24a-497f-bf8d-0184002770c6/artifacts/5nav51b9_logo-print-hd.jpg";

// Liste des liens : module + perm requise pour afficher (read = visible, none = caché)
const NAV_LINKS = [
    { to: '/',                       icon: Home,           label: 'Accueil',               testId: 'sidebar-link-accueil',               module: null },
    { to: '/prospects',              icon: Users,          label: 'Prospects',             testId: 'sidebar-link-prospects',             module: 'prospects' },
    { to: '/calendrier',             icon: CalendarDays,   label: 'Calendrier visite',     testId: 'sidebar-link-calendrier',            module: 'visites_calendrier' },
    { to: '/inventaires',            icon: ClipboardList,  label: 'Inventaire',            testId: 'sidebar-link-inventaires',           module: 'inventaires' },
    { to: '/devis',                  icon: FileText,       label: 'Devis',                 testId: 'sidebar-link-devis',                 module: 'devis' },
    { to: '/clients',                icon: UserCheck,      label: 'Clients',               testId: 'sidebar-link-clients',               module: 'clients' },
    { to: '/calendrier-prestation',  icon: CalendarClock,  label: 'Calendrier prestation', testId: 'sidebar-link-calendrier-prestation', module: 'agenda_prestation' },
    { to: '/aot',                    icon: ShieldCheck,    label: 'AOT',                   testId: 'sidebar-link-aot',                   module: 'aot' },
    { to: '/monte-meuble',           icon: Truck,          label: 'Monte-meuble',          testId: 'sidebar-link-monte-meuble',          module: 'monte_meuble' },
    { to: '/garde-meuble',           icon: Warehouse,      label: 'Garde-meuble',          testId: 'sidebar-link-garde-meuble',          module: 'garde_meuble' },
    { to: '/facturation',            icon: Receipt,        label: 'Facturation',           testId: 'sidebar-link-facturation',           module: 'facturation' },
    { to: '/carte-groupage',         icon: Map,            label: 'Carte groupage',        testId: 'sidebar-link-carte-groupage',        module: 'carte_groupage' },
    { to: '/tarif-aot',              icon: DollarSign,     label: 'Tarif AOT',             testId: 'sidebar-link-tarif-aot',             module: 'tarif_aot' },
    { to: '/exploitation',           icon: HardHat,        label: 'Exploitation',          testId: 'sidebar-link-exploitation',          module: 'exploitation' },
];

export function Sidebar({ onClose }) {
    const { user, logout } = useAuth();
    const { can } = usePermissions();
    const isAdmin = user?.role === 'admin';
    const isPhone = useIsPhone();
    // Sur téléphone (≤ 767px), non-admin : seuls les 2 calendriers sont autorisés
    const phoneAllowedPaths = new Set(['/calendrier', '/calendrier-prestation']);

    const handleLogout = async () => {
        await logout();
        if (onClose) onClose();
    };

    const navLinkClass = ({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg mx-3 mb-1 text-sm font-medium transition-colors ${
            isActive ? 'bg-blue-50 text-[#2e7cd6]' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`;

    return (
        <div className="flex flex-col h-full bg-white">
            <div className="p-6 border-b border-slate-200">
                <img src={LOGO_URL} alt="Logo Big Déménagement" className="w-36 h-auto mx-auto" data-testid="sidebar-logo" />
            </div>

            <nav className="flex-1 py-6 overflow-y-scroll" data-testid="sidebar-nav">
                {NAV_LINKS.map(link => {
                    if (link.module && !can(link.module, 'read')) return null;
                    if (isPhone && !isAdmin && !phoneAllowedPaths.has(link.to)) return null;
                    const Icon = link.icon;
                    return (
                        <NavLink key={link.to} to={link.to} className={navLinkClass} onClick={onClose} data-testid={link.testId} end={link.to === '/'}>
                            <Icon size={20} strokeWidth={2} />
                            <span>{link.label}</span>
                        </NavLink>
                    );
                })}

                {isAdmin && (
                    <NavLink to="/statistiques" className={navLinkClass} onClick={onClose} data-testid="sidebar-link-statistiques">
                        <BarChart3 size={20} strokeWidth={2} />
                        <span>Statistiques</span>
                    </NavLink>
                )}

                {isAdmin && (
                    <NavLink to="/admin" className={navLinkClass} onClick={onClose} data-testid="sidebar-link-admin">
                        <Settings size={20} strokeWidth={2} />
                        <span>Admin</span>
                    </NavLink>
                )}

                <div className="mt-6 pt-4 border-t border-slate-200">
                    <div className="px-4 py-2 mb-2">
                        <p className="text-sm font-medium text-slate-900" data-testid="sidebar-user-name">{user?.name}</p>
                        <p className="text-xs text-slate-500" data-testid="sidebar-user-email">{user?.email}</p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-lg mx-0 w-full text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        data-testid="sidebar-logout-button"
                    >
                        <LogOut size={20} strokeWidth={2} />
                        <span>Déconnexion</span>
                    </button>
                </div>
            </nav>
        </div>
    );
}
